import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { User, MessageCircle, Maximize2, Package, Calendar, Clock, AlertOctagon, AlertTriangle, CheckCircle2, UserMinus } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { WhatsAppButton } from '@/components/whatsapp'
import { useRedemptionDetail } from '@/hooks/useRedemption'
import { isValidWhatsAppNumber } from '@/lib/whatsapp'
import { formatCurrency, maskNRIC } from '@/lib/utils'
import type { RedemptionTimelineEntry } from '@/lib/api-client'

interface Props {
  patientId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kuala_Lumpur',
  })
}

function PromoBreakdownCard({
  masterName,
  promoCode,
  purchased,
  consumed,
  outstanding,
  firstPurchase,
  lastRedemption,
  avgCadenceDays,
  nextDueEstimate,
  pricePaid,
}: {
  masterName: string
  promoCode: string | null
  purchased: number
  consumed: number
  outstanding: number
  firstPurchase: string | null
  lastRedemption: string | null
  avgCadenceDays: number | null
  nextDueEstimate: string | null
  pricePaid: number | null
}) {
  const pct = purchased > 0 ? Math.min(100, Math.round((consumed / purchased) * 100)) : 0
  const isDone = outstanding <= 0
  const headerColor = isDone ? 'text-success' : outstanding >= 3 ? 'text-destructive' : 'text-warning'
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{masterName}</div>
          {promoCode && <div className="text-xs text-text-muted">Promo: {promoCode}</div>}
        </div>
        <div className={`text-2xl font-bold ${headerColor}`}>
          {outstanding > 0 ? `${outstanding} left` : 'Done'}
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-text-muted">
          <span>{consumed} of {purchased} sessions consumed</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full ${isDone ? 'bg-success' : outstanding >= 3 ? 'bg-destructive' : 'bg-warning'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs pt-1">
        <div className="text-text-muted">First buy</div>
        <div className="font-medium text-right">{formatDate(firstPurchase)}</div>

        <div className="text-text-muted">Last redeem</div>
        <div className="font-medium text-right">{formatDate(lastRedemption)}</div>

        <div className="text-text-muted">Avg cadence</div>
        <div className="font-medium text-right">{avgCadenceDays ? `${avgCadenceDays} days` : '—'}</div>

        <div className="text-text-muted">Next due ~</div>
        <div className="font-medium text-right">{formatDate(nextDueEstimate)}</div>

        {pricePaid && (
          <>
            <div className="text-text-muted">Package price</div>
            <div className="font-medium text-right">{formatCurrency(pricePaid)}</div>
          </>
        )}
      </div>
    </div>
  )
}

// Group timeline entries by invoice (one invoice can have multiple line items)
function groupedTimeline(entries: RedemptionTimelineEntry[]) {
  const byInvoice = new Map<string, { invoiceNo: string; date: string; grandTotal: number; items: RedemptionTimelineEntry[] }>()
  for (const e of entries) {
    if (!byInvoice.has(e.invoiceNo)) {
      byInvoice.set(e.invoiceNo, { invoiceNo: e.invoiceNo, date: e.invoiceDate, grandTotal: e.grandTotal, items: [] })
    }
    byInvoice.get(e.invoiceNo)!.items.push(e)
  }
  return Array.from(byInvoice.values())
}

function TimelineEntry({ inv }: { inv: { invoiceNo: string; date: string; grandTotal: number; items: RedemptionTimelineEntry[] } }) {
  const isPurchase = inv.grandTotal > 0
  const allFoc = inv.items.every((i) => i.isFoc)
  let icon: string
  let dotColor: string
  if (isPurchase) { icon = '💰'; dotColor = 'bg-info' }
  else if (allFoc) { icon = '🆓'; dotColor = 'bg-text-muted' }
  else { icon = '✓'; dotColor = 'bg-success' }

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-2 h-2 rounded-full ${dotColor} mt-2`} />
        <div className="flex-1 w-px bg-border mt-1" />
      </div>
      <div className="flex-1 pb-4 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-medium">
            {icon} {formatDate(inv.date)}
          </div>
          <div className={`text-xs font-mono ${isPurchase ? 'text-info' : 'text-text-muted'}`}>
            {isPurchase ? formatCurrency(inv.grandTotal) : 'RM 0.00'}
          </div>
        </div>
        <div className="text-xs text-text-muted mb-1">{inv.invoiceNo}</div>
        <div className="space-y-0.5">
          {inv.items.map((it, idx) => (
            <div key={idx} className="text-xs flex items-baseline gap-2">
              <span className="text-foreground/80 truncate" title={it.rawTreatmentName}>
                {it.isFoc && <span className="text-text-muted">[FOC] </span>}
                {it.rawTreatmentName}
              </span>
              {it.quantity > 1 && <span className="text-text-muted">×{it.quantity}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function RedemptionSheet({ patientId, open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const { data, isPending } = useRedemptionDetail(patientId)

  const patient = data?.patient
  const breakdown = data?.breakdown ?? []
  const timeline = data?.timeline ?? []
  const grouped = React.useMemo(() => groupedTimeline(timeline), [timeline])

  const outstanding = breakdown.filter((b) => b.outstanding > 0)
  const done = breakdown.filter((b) => b.outstanding <= 0)
  const totalOutstanding = outstanding.reduce((s, b) => s + b.outstanding, 0)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {isPending ? <Skeleton className="h-6 w-48" /> : patient?.name ?? 'Patient Details'}
          </SheetTitle>
          <SheetDescription>
            {isPending ? 'Loading…' : <>PNO {patient?.pno} · {patient?.branchName}</>}
          </SheetDescription>
        </SheetHeader>

        {/* Action bar */}
        {patientId && patient && (
          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                onOpenChange(false)
                navigate({ to: '/patients/$id', params: { id: String(patientId) } })
              }}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              Full Profile
            </Button>
            {patient.phone && isValidWhatsAppNumber(patient.phone) && (
              <WhatsAppButton
                phone={patient.phone}
                patientName={patient.name}
                branchName={patient.branchName ?? undefined}
              />
            )}
          </div>
        )}

        {isPending ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : patient ? (
          <div className="space-y-6 mt-6">
            {/* NRIC + phone */}
            {(patient.nric || patient.phone) && (
              <div className="rounded-lg bg-muted p-3 text-xs space-y-1.5">
                {patient.nric && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-text-muted">NRIC</span>
                    <span className="font-mono text-foreground">{maskNRIC(patient.nric)}</span>
                  </div>
                )}
                {patient.phone && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-text-muted">Phone</span>
                    <span className="font-mono text-foreground">{patient.phone}</span>
                  </div>
                )}
              </div>
            )}

            {/* Outstanding breakdown */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Outstanding Packages
                </h3>
                <div className="text-xs text-text-muted">
                  {totalOutstanding} sessions across {outstanding.length} package{outstanding.length === 1 ? '' : 's'}
                </div>
              </div>
              {outstanding.length === 0 ? (
                <div className="rounded-lg border border-success/30 bg-success/5 p-4 text-sm text-text-muted">
                  No outstanding packages. All multi-session packages fully redeemed.
                </div>
              ) : (
                outstanding.map((b) => (
                  <PromoBreakdownCard key={`${b.treatmentId}-${b.promoCode}`} {...b} />
                ))
              )}
            </div>

            {/* Completed (collapsed) */}
            {done.length > 0 && (
              <details className="space-y-3">
                <summary className="text-sm font-medium cursor-pointer text-text-muted hover:text-foreground">
                  <CheckCircle2 className="w-4 h-4 inline mr-2" />
                  Completed packages ({done.length})
                </summary>
                <div className="space-y-2 mt-3">
                  {done.map((b) => (
                    <PromoBreakdownCard key={`${b.treatmentId}-${b.promoCode}`} {...b} />
                  ))}
                </div>
              </details>
            )}

            {/* Visit timeline */}
            {grouped.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Visit Timeline ({grouped.length} invoice{grouped.length === 1 ? '' : 's'})
                </h3>
                <div className="space-y-0">
                  {grouped.map((inv) => (
                    <TimelineEntry key={inv.invoiceNo} inv={inv} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-text-muted mt-6">Failed to load patient detail.</div>
        )}
      </SheetContent>
    </Sheet>
  )
}
